import { MouseEvent, ReactElement, ReactNode } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled, { css } from 'styled-components'

import { variables } from 'src/Styled/variables'
import Loader from 'src/Assets/Svg/loader.svg?react'
import { DEBOUNCE_DELAY_100 } from 'src/Constants/constants'

export interface AuthOutlineButtonProps {
  icon?: ReactNode
  label: string
  loading?: boolean
  disabled?: boolean
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  className?: string
}

const StyledButton = styled.button<{ $loading?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 48px;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
  gap: 4px;
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  color: ${({ theme }) => theme.color.darkGrey};

  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  cursor: pointer;

  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.color.darkGreen};
    outline-offset: -1px;
    background: ${({ theme }) => theme.color.hoverWhite};
  }

  &:active {
    outline: none;
    border-color: ${({ theme }) => theme.color.mediumGrey};
    color: ${({ theme }) => theme.color.slateGrey};
  }

  ${({ $loading }) =>
    !$loading &&
    css`
      &:disabled {
        color: ${({ theme }) => theme.color.mediumGrey};
        border-color: ${({ theme }) => theme.color.lightGrey};
        cursor: not-allowed;
        background: ${({ theme }) => theme.color.white};
      }
    `}
`

const LoaderWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;

    path {
      fill: ${({ theme }) => theme.color.darkGrey};
    }
  }
`

const ContentWrapper = styled.div<{ $loading?: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  display: flex;
  align-items: center;
  gap: 8px;
`

export const AuthOutlineButton = ({
  icon,
  label,
  loading,
  disabled,
  onClick,
  className,
}: AuthOutlineButtonProps): ReactElement => {
  const debouncedOnClick = useDebouncedCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (typeof onClick === 'function') {
        onClick(event)
      }
    },
    DEBOUNCE_DELAY_100,
  )

  return (
    <StyledButton
      type='button'
      className={className}
      $loading={loading}
      disabled={disabled || loading}
      onClick={(e) => debouncedOnClick(e)}
    >
      {loading && (
        <LoaderWrapper>
          <Loader data-testid='loader' />
        </LoaderWrapper>
      )}

      <ContentWrapper $loading={loading}>
        {icon}
        {label}
      </ContentWrapper>
    </StyledButton>
  )
}
